import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { createTeamMember, updateTeamMember, fetchTeams, fetchRoles, fetchTeamMembers, selectTeams, selectRoles, selectTeamMembers } from '../../store/slices/teamSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import Inputs from '../../components/inputs';
import { setUserGoal } from '../../services/team.service';

interface TeamMemberFormProps {
  member?: any;
  onClose: () => void;
}

const TeamMemberForm: React.FC<TeamMemberFormProps> = ({ member, onClose }) => {
  const dispatch = useAppDispatch();
  const teams = useAppSelector(selectTeams);
  const roles = useAppSelector(selectRoles);
  const teamMembers = useAppSelector(selectTeamMembers);
  const userTeamMembers = useAppSelector((state) => state.whatsapp.user.teamMembers);

  // Handle teams initialization based on mobile pattern
  const initializeTeams = (): string | string[] => {
    if (!member) return 'organisation'; // Default for new users

    // API returns both 'team' (single ID) and 'teams' (array of team objects)
    let teamIds: string[] = [];

    // Extract team IDs from teams array (if present)
    if (member.teams && Array.isArray(member.teams) && member.teams.length > 0) {
      teamIds = member.teams.map((t: any) => t._id || t).filter(Boolean);
    }
    // Fallback to single team field
    else if (member.team) {
      teamIds = Array.isArray(member.team) ? member.team.filter(Boolean) : [member.team].filter(Boolean);
    }

    // If organization member, ALWAYS include 'organisation'
    if (member.isOrganizationMember) {
      // If they belong to additional teams, return array with both org and teams
      if (teamIds.length > 0) {
        return ['organisation', ...teamIds];
      }
      // If only org member, return just 'organisation' string
      return 'organisation';
    }

    // Not an org member, return only custom team IDs
    return teamIds.length > 0 ? teamIds : [];
  };

  // Extract goal values from userGoal array
  const getGoalValue = (type: 'sales_amount' | 'calls', frequency: 'monthly' | 'quarterly' = 'monthly') => {
    if (!member?.userGoal) return '';
    const goal = member.userGoal.find((g: any) => g.type === type && g.frequency === frequency);
    return goal?.target || '';
  };

  const initialFormData = {
    firstName: member?.firstName || '',
    lastName: member?.lastName || '',
    email: member?.email || '',
    phone: member?.phone || '',
    countryCode: member?.countryCode || '+91',
    role: member?.role || '',
    teams: initializeTeams(),
    reportTo: member?.reportTo || '',
    salesTarget: getGoalValue('sales_amount', 'monthly'),
    callsTarget: getGoalValue('calls', 'monthly'),
    isActive: member?.isActive !== false
  };

  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if form data has changed
  const hasFormChanged = () => {
    if (!member) return true; // New user, always save
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  };

  // Combine team members from both sources (needed for handleInputChange)
  const allTeamMembers = [
    ...(teamMembers || []),
    ...(userTeamMembers?.map(utmember => ({
      _id: utmember.value,
      firstName: utmember.label.split(' ')[0],
      lastName: utmember.label.split(' ').slice(1).join(' '),
      role: '' // Will be filtered by role later
    })) || [])
  ];

  useEffect(() => {
    // Only fetch teams if not already loaded
    if (!teams || teams.length === 0) {
      dispatch(fetchTeams({ page: 1, perPage: 100 }));
    }

    // Only fetch roles if not already loaded
    if (!roles || roles.length === 0) {
      dispatch(fetchRoles());
    }

    // Always fetch team members when form opens to ensure reportTo manager is present
    // This is critical for populating the reports-to dropdown correctly
    dispatch(fetchTeamMembers({ page: 1, perPage: 500 }));
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Special handling for reportTo - convert name to ID
    if (name === 'reportTo') {
      const manager = allTeamMembers.find(m =>
        `${m.firstName || ''} ${m.lastName || ''}`.trim() === value
      );
      setFormData(prev => ({
        ...prev,
        reportTo: manager?._id || value
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle role change - affects team multiselect and reports-to
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const roleName = e.target.value;
    const role = roles?.find(r => (r.displayName?.split('_').join(' ') || r.name) === roleName);
    const roleId = role?._id || '';

    setFormData(prev => ({
      ...prev,
      role: roleId, // Store role ID internally
      // Reset teams when role changes
      teams: prev.teams === 'organisation' ? 'organisation' : []
    }));
  };

  // Handle team change - affects reports-to field visibility
  const handleTeamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Map team name to team ID/value
    let teamValue = value;
    if (value === organizationName) {
      teamValue = 'organisation';
    } else {
      // Find team ID by name
      const team = teams?.find(t => t.name === value);
      teamValue = team?._id || value;
    }

    setFormData(prev => ({
      ...prev,
      [name]: teamValue,
      // Clear reports-to if switching to custom team
      reportTo: teamValue === 'organisation' ? prev.reportTo : ''
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if form has changed (prevent unnecessary API calls)
    if (!hasFormChanged()) {
      onClose();
      return;
    }

    // Validation
    if (!formData.firstName.trim()) {
      alert('First name is required');
      return;
    }

    if (!formData.phone.trim()) {
      alert('Phone number is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const memberData: any = {
        ...formData,
        // Send firstName and lastName (not name)
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        // Clean up data
        email: formData.email.trim() || undefined, // Email is optional
        phone: formData.phone.trim()
      };

      // Handle organization vs custom team logic (exact mobile pattern)
      if (!memberData.teams || memberData.teams.length === 0 || memberData.teams.includes('organisation')) {
        memberData.isOrganizationMember = true;
        if (Array.isArray(memberData.teams)) {
          memberData.teams = memberData.teams.filter((item: any) => item !== 'organisation');
        } else if (memberData.teams) {
          memberData.teams = [];
        } else {
          memberData.teams = [];
        }
      } else {
        memberData.isOrganizationMember = false;
        delete memberData.reportTo; // Remove reportTo if not organization member
        if (!Array.isArray(memberData.teams)) {
          memberData.teams = [memberData.teams];
        }
      }

      // Set default reportTo for new organization members (mobile pattern)
      if (!member && memberData.isOrganizationMember && !memberData.reportTo) {
        const superAdminId = getSuperAdminId();
        if (superAdminId) {
          memberData.reportTo = superAdminId;
        }
      }

      // Remove goal fields from member data (handled separately like mobile)
      delete memberData.salesTarget;
      delete memberData.callsTarget;

      let response;
      if (member) {
        // Update existing member
        response = await dispatch(updateTeamMember({ ...memberData, _id: member._id }));

        // After successful update, handle goals separately (mobile pattern)
        if (response.meta.requestStatus === 'fulfilled') {
          const goalBody: any[] = [];

          // Add sales target goal if present
          if (formData.salesTarget || formData.salesTarget === '0') {
            goalBody.push({
              type: 'sales_amount',
              target: Number(formData.salesTarget),
              frequency: 'monthly',
              userId: member._id
            });
          }

          // Add calls target goal if present
          if (formData.callsTarget || formData.callsTarget === '0') {
            goalBody.push({
              type: 'calls',
              target: Number(formData.callsTarget),
              frequency: 'monthly',
              userId: member._id
            });
          }

          // Only call setUserGoal if there are goals to update
          if (goalBody.length > 0) {
            await setUserGoal(goalBody);
          }
        }
      } else {
        // Create new member (don't send goals in create)
        delete memberData.isActive; // Mobile pattern: don't send isActive on create
        response = await dispatch(createTeamMember(memberData));
      }

      // Close form only if operation was successful
      if (response && response.meta.requestStatus === 'fulfilled') {
        onClose();
      }
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Error saving team member. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get current user organization for dropdown
  const currentUser = useAppSelector((state) => state.whatsapp.user.currentUser);
  const organizationName = currentUser?.organization?.name || 'Organization';

  // Prepare dropdown options
  const teamOptions = [organizationName, ...(teams?.map(team => team.name) || [])];
  const roleOptionsForDropdown = roles?.filter(role => role.name !== 'super_admin') || [];

  // Check if current role is admin (for multi-select logic)
  const selectedRole = roles?.find(r => r._id === formData.role);
  const isAdminRole = selectedRole?.name === 'admin';

  // Get super admin ID for default reportTo (mobile pattern)
  const getSuperAdminId = () => {
    const superAdminRole = roles?.find(role => role.name === 'super_admin');
    if (!superAdminRole) return null;

    const superAdmin = teamMembers?.find(member => member.role === superAdminRole._id);
    return superAdmin?._id || null;
  };

  // Reports-to options (only admin, team leader, super admin roles)
  const getManagerRoles = () => {
    if (!roles) return [];
    return roles
      .filter(role => {
        const roleName = role.name?.toLowerCase() || '';
        return roleName.includes('admin') || roleName.includes('team_leader') || roleName.includes('super_admin');
      })
      .map(role => role._id);
  };

  const managerRoleIds = getManagerRoles();

  const reportsToOptions = allTeamMembers
    ?.filter(teamMember => {
      // Exclude self if editing
      if (member && teamMember._id === member._id) return false;

      // Only include members with manager roles
      return managerRoleIds.includes(teamMember.role);
    })
    ?.map(teamMember => `${teamMember.firstName || ''} ${teamMember.lastName || ''}`.trim())
    ?.filter(name => name.length > 0) || [];

  // Check if user is organization member (for conditional display)
  const isOrgMember = formData.teams === 'organisation' ||
    (Array.isArray(formData.teams) && formData.teams.includes('organisation'));

  // Get reportTo display value (convert ID to name)
  const getReportToDisplayValue = () => {
    if (!formData.reportTo) return '';
    const manager = allTeamMembers.find(m => m._id === formData.reportTo);
    if (manager) {
      return `${manager.firstName || ''} ${manager.lastName || ''}`.trim();
    }
    // If manager not found in options, return empty string (will show placeholder)
    return '';
  };

  return (
    <form onSubmit={handleSubmit} className="team-member-form">
        <div className="form-body">
          <div className="form-row">
            <div className="form-col">
              <Inputs
                label="First Name"
                name="firstName"
                type="name"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Enter first name"
                className="form-control"
                autoComplete="given-name"
                isRequired={true}
                options={[]}
                id="firstName"
              />
            </div>
            <div className="form-col">
              <Inputs
                label="Last Name"
                name="lastName"
                type="name"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Enter last name"
                className="form-control"
                autoComplete="family-name"
                isRequired={false}
                options={[]}
                id="lastName"
              />
            </div>
          </div>

          <Inputs
            label="Phone Number"
            name="phone"
            type="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Enter phone number"
            className="form-control"
            autoComplete="tel"
            isRequired={true}
            options={[]}
            id="phone"
          />

          <Inputs
            label="Email (Optional)"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Enter email address"
            className="form-control"
            autoComplete="email"
            isRequired={false}
            options={[]}
            id="email"
          />

          {/* Role - Always show first to determine other field behavior */}
          {roleOptionsForDropdown.length > 0 && (
            <Inputs
              label="Role"
              name="role"
              type="selection"
              value={roles?.find(r => r._id === formData.role)?.displayName?.split('_').join(' ') || roles?.find(r => r._id === formData.role)?.name || ''}
              onChange={handleRoleChange}
              placeholder="Select role"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={roleOptionsForDropdown.map(role => role.displayName?.split('_').join(' ') || role.name)}
              id="role"
            />
          )}

          {/* Teams - Show only if role is selected, behavior changes based on role */}
          {formData.role && teamOptions.length > 0 && (
            <Inputs
              label="Teams"
              name="teams"
              type={isAdminRole ? "multi-selection" : "selection"}
              value={
                formData.teams === 'organisation'
                  ? organizationName
                  : Array.isArray(formData.teams)
                    ? formData.teams.map(teamId =>
                        teamId === 'organisation'
                          ? organizationName
                          : teams?.find(t => t._id === teamId)?.name || teamId
                      )
                    : teams?.find(t => t._id === formData.teams)?.name || formData.teams
              }
              onChange={handleTeamChange}
              placeholder={isAdminRole ? "Select teams (multiple allowed)" : "Select team"}
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={teamOptions}
              id="teams"
            />
          )}

          {/* Reports To - Only show for organization members */}
          {formData.role && isOrgMember && reportsToOptions.length > 0 && (
            <Inputs
              label="Reports To"
              name="reportTo"
              type="selection"
              value={getReportToDisplayValue()}
              onChange={handleInputChange}
              placeholder="Select manager"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={reportsToOptions}
              id="reportTo"
            />
          )}

          {/* Sales Target (Monthly) */}
          <Inputs
            label="Sales Target (Monthly)"
            name="salesTarget"
            type="number"
            value={formData.salesTarget}
            onChange={handleInputChange}
            placeholder="Enter monthly sales target"
            className="form-control"
            autoComplete="off"
            isRequired={false}
            options={[]}
            id="salesTarget"
          />

          {/* Calls Target (Monthly) */}
          <Inputs
            label="Calls Target (Monthly)"
            name="callsTarget"
            type="number"
            value={formData.callsTarget}
            onChange={handleInputChange}
            placeholder="Enter monthly calls target"
            className="form-control"
            autoComplete="off"
            isRequired={false}
            options={[]}
            id="callsTarget"
          />

          <div className="form-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              <span>Active Member</span>
            </label>
          </div>
        </div>

        <div className="form-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isSubmitting || !formData.firstName.trim() || !formData.phone.trim() || !formData.role}
          >
            <Save size={16} />
            {isSubmitting ? 'Saving...' : member ? 'Update Member' : 'Add Member'}
          </button>
        </div>
      </form>
  );
};

export default TeamMemberForm;